// ═══════════════════════════════════════════════════════════════════════════
// Per-action translators — BASE-PRIMITIVE + BASE-MODIFY (CC-2c sub-arm 4)
//                                                          — PACKAGE-INTERNAL
// ═══════════════════════════════════════════════════════════════════════════
//
// The thin, total per-action translators behind the BASE-PRIMITIVE (create) +
// BASE-MODIFY domain (CREATE_LINE/CIRCLE/ARC/PLINE + MOVE/COPY/ROTATE). Each AI
// shape maps 1:1 (or 1:N for COPY multi-drop) onto the corresponding contract
// `EditorCommand`, with no simplification — full required params per the
// EditorCommand + ТЗ.
//
// IMPORTANT (AI safe contour): these translators produce geometry
// `EditorCommand`s WITHOUT running the CC-2a fail-closed guard
// (`assertAiCallableBasePrimAction`). They are therefore PACKAGE-INTERNAL: this
// file is NOT re-exported by the barrel (`index.ts`). The ONLY public base-prim
// entry is the guarded `translateBasePrimCommand` in `basePrimDispatcher.ts`
// (which imports these). This mirrors the BLOCK domain (non-barrelled
// `blockDispatcher*.ts`), the ENTITY domain (`entityTranslators.ts`) and the
// DRAW-MODIFY domain (`drawModifyTranslators.ts`) — so a consumer of
// `@dxf-ai-editor/ai-commands` can never emit `geometry/create-line` etc. past
// the guard. Honours the CLAUDE.md rules:
//   "AI drafting … может только предлагать и исполнять команды через
//    безопасный контур." + "любые изменения геометрии только через Command
//    Engine."

import type { EditorCommand } from "@dxf-ai-editor/contracts";

import {
  AI_COMMAND_SOURCE,
  pickSpaceId,
  type AiDispatchContext,
} from "./blockDispatcherShared";
import type {
  CopyAiCommand,
  CreateArcAiCommand,
  CreateCircleAiCommand,
  CreateLineAiCommand,
  CreatePlineAiCommand,
  DeleteEntitiesAiCommand,
  MoveAiCommand,
  RotateAiCommand,
} from "./basePrimSchemas";

// ═══════════════════════════════════════════════════════════════════════════
// BASE PRIMITIVES (create) — spaceId resolved via the dispatch context.
// ═══════════════════════════════════════════════════════════════════════════

// ─── CREATE_LINE ────────────────────────────────────────────────────────────
export function translateCreateLine(
  command: CreateLineAiCommand,
  context: AiDispatchContext,
): EditorCommand[] {
  return [
    {
      type: "geometry/create-line",
      source: AI_COMMAND_SOURCE,
      spaceId: pickSpaceId(command, context),
      start: { x: command.start.x, y: command.start.y, z: command.start.z },
      end: { x: command.end.x, y: command.end.y, z: command.end.z },
    },
  ];
}

// ─── CREATE_CIRCLE ──────────────────────────────────────────────────────────
export function translateCreateCircle(
  command: CreateCircleAiCommand,
  context: AiDispatchContext,
): EditorCommand[] {
  return [
    {
      type: "geometry/create-circle",
      source: AI_COMMAND_SOURCE,
      spaceId: pickSpaceId(command, context),
      center: { x: command.center.x, y: command.center.y, z: command.center.z },
      radius: command.radius,
    },
  ];
}

// ─── CREATE_ARC ─────────────────────────────────────────────────────────────
//
// startAngle / endAngle flow through in DEGREES verbatim (the reducer stores
// them unchanged; DXF group codes 50/51 are degrees).
export function translateCreateArc(
  command: CreateArcAiCommand,
  context: AiDispatchContext,
): EditorCommand[] {
  return [
    {
      type: "geometry/create-arc",
      source: AI_COMMAND_SOURCE,
      spaceId: pickSpaceId(command, context),
      center: { x: command.center.x, y: command.center.y, z: command.center.z },
      radius: command.radius,
      startAngle: command.startAngle,
      endAngle: command.endAngle,
    },
  ];
}

// ─── CREATE_PLINE ───────────────────────────────────────────────────────────
//
// Full per-vertex fidelity (bulge / startWidth / endWidth) — the LWPOLYLINE is
// not flattened to a bare point list.
export function translateCreatePline(
  command: CreatePlineAiCommand,
  context: AiDispatchContext,
): EditorCommand[] {
  return [
    {
      type: "geometry/create-lwpolyline",
      source: AI_COMMAND_SOURCE,
      spaceId: pickSpaceId(command, context),
      vertices: command.vertices.map((v) => ({
        x: v.x,
        y: v.y,
        bulge: v.bulge,
        startWidth: v.startWidth,
        endWidth: v.endWidth,
      })),
      closed: command.closed,
    },
  ];
}

// ═══════════════════════════════════════════════════════════════════════════
// BASE MODIFY — self-contained (entity ids); no dispatch context needed.
// ═══════════════════════════════════════════════════════════════════════════

// ─── MOVE ───────────────────────────────────────────────────────────────────
//
// delta = secondPoint − basePoint (AutoCAD's two-point displacement).
export function translateMove(command: MoveAiCommand): EditorCommand[] {
  return [
    {
      type: "geometry/move-entities",
      source: AI_COMMAND_SOURCE,
      entityIds: command.entityIds,
      delta: {
        x: command.secondPoint.x - command.basePoint.x,
        y: command.secondPoint.y - command.basePoint.y,
      },
    },
  ];
}

// ─── COPY (1:N multi-drop) ───────────────────────────────────────────────────
//
// One `geometry/copy` per destination, carrying the explicit base + second
// point pair — IDENTICAL to the manual COPY commit emitted by the draft state
// machine (`draftModifyStateMachine.ts` → `geometry/copy`). This is the SINGLE
// mutating COPY path (single source of truth): the
// `resolveGeometryDocumentCommand` allowlist routes `geometry/copy` to
// `reduceGeometryDocument`, which clones every source entity translated by
// (secondPoint − basePoint) and APPENDS the clones to the document. The
// originals stay put; N offset clones are produced (AutoCAD COPY multi-drop).
// The schema guarantees ≥1 destination, so ≥1 clone set is always created.
//
// WHY NOT `geometry/copy-entities` (the delta-only sibling): that command is
// audit-only in the shell reducer and is NOT in the geometry-document allowlist,
// so emitting it produces ZERO actual clones (the gap this fix closes). Routing
// AI COPY through `geometry/copy` keeps it on the SAME Command-Engine mutation +
// undo path as the manual command and is invertible (computeInverse →
// `shell/delete-entities` of the appended clone ids; one undo per turn via the
// safe-contour batch).
export function translateCopy(command: CopyAiCommand): EditorCommand[] {
  return command.secondPoints.map((secondPoint) => ({
    type: "geometry/copy",
    source: AI_COMMAND_SOURCE,
    sourceEntityIds: command.entityIds,
    basePoint: { x: command.basePoint.x, y: command.basePoint.y, z: 0 },
    secondPoint: { x: secondPoint.x, y: secondPoint.y, z: 0 },
  }));
}

// ─── ROTATE ──────────────────────────────────────────────────────────────────
//
// angleDeg flows through in DEGREES verbatim — the reducer's rotatePoint
// converts degrees → radians internally (converting here would double-convert).
export function translateRotate(command: RotateAiCommand): EditorCommand[] {
  return [
    {
      type: "geometry/rotate-entities",
      source: AI_COMMAND_SOURCE,
      entityIds: command.entityIds,
      basePoint: { x: command.basePoint.x, y: command.basePoint.y },
      angleDeg: command.angleDeg,
    },
  ];
}

// ─── DELETE_ENTITIES (ERASE) ─────────────────────────────────────────────────
//
// One `shell/delete-entities` command carrying the agent-supplied `entityIds` —
// IDENTICAL to the Delete-key path (useKeyboardShortcuts.ts dispatches the SAME
// command type). This is the single delete mutation path (single source of
// truth): the geometry reducer removes every matching entity and SKIPS unknown /
// stale / foreign ids gracefully (no-op, no throw — verified in
// geometryReducer.ts: ids that match nothing yield NO_MUTATIONS). It is
// invertible (`computeInverse` → `shell/restore-entities` of the snapshotted
// entities), and the safe-contour's begin-batch/commit-batch wraps the turn so
// it is ONE undo entry — no manual batching here. The schema guarantees ≥1 id.
export function translateDeleteEntities(
  command: DeleteEntitiesAiCommand,
): EditorCommand[] {
  return [
    {
      type: "shell/delete-entities",
      source: AI_COMMAND_SOURCE,
      entityIds: command.entityIds,
    },
  ];
}
